/**
 * Discord Activities (author's addition): the game in Discord's frame, served by its proxy at <client id>.discordsays.com,
 * whose URL mapping `/` points to the game's server (the pages, the scripts and the relay all go through it). Discord
 * opens the Activity with `frame_id`, `instance_id` and `platform` in the address: the instance is shared by everyone
 * who plays it together, and is the key of their room (the server's `find`).
 */
const params = new URLSearchParams(location.search);

/** The Activity's instance, null outside Discord. */
export const discordInstance: string | null = params.has('frame_id') ? params.get('instance_id') : null;

type Sdk = import('@discord/embedded-app-sdk').DiscordSDK;

/** The SDK after its handshake, and the app's id; null outside Discord's frame or if the handshake failed. */
let started: Promise<{ sdk: Sdk; clientId: string } | null> | null = null;

/**
 * The handshake Discord waits for, through its SDK, loaded only in Discord (the website never downloads it). Outside
 * Discord's frame (a test with its parameters in the address of a plain tab), there is nobody to shake hands with.
 * Done once, whoever asks first.
 */
export function startDiscord(): Promise<{ sdk: Sdk; clientId: string } | null> {
  started ??= shakeHands();
  return started;
}

async function shakeHands(): Promise<{ sdk: Sdk; clientId: string } | null> {
  if (discordInstance === null || window.parent === window) return null;
  // The client id is in the proxy's address; through a tunnel (Application URL Override), it is given at the build.
  const host = location.hostname;
  const clientId = host.endsWith('.discordsays.com') ? host.split('.')[0] : (import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined);
  if (!clientId) { console.warn('Discord: no client id (VITE_DISCORD_CLIENT_ID)'); return null; }
  try {
    const { DiscordSDK } = await import('@discord/embedded-app-sdk');
    const sdk = new DiscordSDK(clientId);
    await sdk.ready();
    return { sdk, clientId };
  } catch (e) {
    console.warn('Discord: the handshake failed', e);
    return null;
  }
}

/**
 * The code of the player's authorization (author's mode: its Discord name is its team name), for the server, which
 * reads the name with it and the app's secret. Discord asks the player once (a window: « identify »), then answers
 * without asking. A code is good once: one per connection to the server. Null outside Discord's frame, or if the player
 * refused.
 */
export async function discordCode(): Promise<string | null> {
  const discord = await startDiscord();
  if (!discord) return null;
  try {
    const { code } = await discord.sdk.commands.authorize({ client_id: discord.clientId, response_type: 'code', state: '', prompt: 'none', scope: ['identify'] });
    return code;
  } catch (e) {
    console.warn('Discord: no authorization', e);
    return null;
  }
}
